import {
  type CanadaProvinceCode,
  type UsaStateCode,
} from '@webapp/core/entities/enums/StateProvince'
import { type CountryCode } from '@webapp/core/entities/external/Country'
import {
  Currency,
  type CurrencyConstructor,
} from '@webapp/core/entities/external/Currency'
import {
  PaymentMethodTypes,
  type PaymentMethodTypeEnum,
} from '../enums/PaymentMethodTypeEnum'

const DOMESTIC_US_PAYMENT_RAILS = ['ACH', 'FEDWIRE', 'PAPER_CHECK']

export interface TransactionFee {
  type: 'percentage' | 'fixed'
  value: string
}

export interface PaymentMethodOtherData {
  description?: string
}

export interface PaymentMethodCheckData {
  payableTo: string
}

export interface PaymentMethodACHData {
  routingNumber: string
  companyName: string
  accountNumber: string
}

export interface PaymentMethodEFTData {
  bankAccountHolderName: string
  bankName: string | null
  institutionNumber: string
  transitNumber: string
  accountNumber: string
  payeeName: string
  streetAddress: string
  city: string
  province: CanadaProvinceCode
  postalCode: string
}

export interface PaymentMethodProcurifyAchData {
  routingNumber: string
  payeeName: string
  accountNumber: string
  bankAccountHolderName: string
  bankName: string | null
  streetAddress: string
  city: string
  state: UsaStateCode
  zipCode: string
}

export interface PaymentMethodProcurifyCheckData {
  payeeName: string
  streetAddress: string
  city: string
  state: UsaStateCode
  zipCode: string
}

export interface PaymentMethodAirwallexData {
  accountCurrency: string
  accountName: string
  accountNameAlias?: string
  accountNumber?: string
  accountRoutingType1?: string
  accountRoutingValue1?: string
  accountRoutingType2?: string
  accountRoutingValue2?: string
  bankAccountCategory?: string
  bankBranch?: string
  bankCountryCode: string
  bankState?: string
  bindingMobileNumber?: string
  companyName?: string
  localClearingSystem?: string
  entityType?: string
  fingerprint?: string
  iban?: string
  intermediaryBankName?: string
  intermediaryBankSwiftCode?: string
  swiftCode?: string
  address: {
    city?: string
    countryCode?: CountryCode
    postcode?: string
    state?: string
    streetAddress?: string
  }
}

// Union type for all data types
export type PaymentMethodData =
  | PaymentMethodOtherData
  | PaymentMethodCheckData
  | PaymentMethodACHData
  | PaymentMethodEFTData
  | PaymentMethodProcurifyAchData
  | PaymentMethodProcurifyCheckData
  | PaymentMethodAirwallexData

// Base constructor interface
interface BasePaymentMethodConstructor {
  id: number
  name: string
  currency: CurrencyConstructor
  vendorId: number
  paymentRail: string
  bankAccountCountryCode: Nullable<CountryCode>
  flightTime: Nullable<{
    min: number
    max: number
  }>
  transactionFee: Nullable<TransactionFee>
}

export interface PaymentMethodOtherConstructor
  extends BasePaymentMethodConstructor {
  type: typeof PaymentMethodTypes.OTHER
  data: PaymentMethodOtherData
}

export interface PaymentMethodCheckConstructor
  extends BasePaymentMethodConstructor {
  type: typeof PaymentMethodTypes.CHECK
  data: PaymentMethodCheckData
}

export interface PaymentMethodACHConstructor
  extends BasePaymentMethodConstructor {
  type: typeof PaymentMethodTypes.ACH
  data: PaymentMethodACHData
}

export interface PaymentMethodEFTConstructor
  extends BasePaymentMethodConstructor {
  type: typeof PaymentMethodTypes.EFT
  data: PaymentMethodEFTData
}

export interface PaymentMethodProcurifyAchConstructor
  extends BasePaymentMethodConstructor {
  type:
    | typeof PaymentMethodTypes.PROCURIFY_ACH
    | typeof PaymentMethodTypes.PROCURIFY_WIRE
  data: PaymentMethodProcurifyAchData
}

export interface PaymentMethodProcurifyCheckConstructor
  extends BasePaymentMethodConstructor {
  type: typeof PaymentMethodTypes.PROCURIFY_CHECK
  data: PaymentMethodProcurifyCheckData
}

export interface PaymentMethodAirwallexConstructor
  extends BasePaymentMethodConstructor {
  type: typeof PaymentMethodTypes.AIRWALLEX
  data: PaymentMethodAirwallexData
}

export type PaymentMethodConstructor =
  | PaymentMethodOtherConstructor
  | PaymentMethodCheckConstructor
  | PaymentMethodACHConstructor
  | PaymentMethodEFTConstructor
  | PaymentMethodProcurifyAchConstructor
  | PaymentMethodProcurifyCheckConstructor
  | PaymentMethodAirwallexConstructor

export class PaymentMethod {
  readonly id: number
  readonly name: string
  readonly type: PaymentMethodTypeEnum
  readonly currency: Currency
  readonly data: PaymentMethodData
  readonly vendorId: number
  readonly paymentRail: string
  readonly bankAccountCountryCode: Nullable<CountryCode>
  readonly flightTime: Nullable<{
    min: number
    max: number
  }>
  readonly transactionFee: Nullable<TransactionFee>

  constructor(data: PaymentMethodConstructor) {
    this.id = data.id
    this.name = data.name
    this.type = data.type
    this.currency = new Currency(data.currency)
    this.data = data.data
    this.vendorId = data.vendorId
    this.paymentRail = data.paymentRail
    this.bankAccountCountryCode = data.bankAccountCountryCode
    this.flightTime = data.flightTime
    this.transactionFee = data.transactionFee
  }

  // Type guard methods for type-safe data access
  isOther(): this is PaymentMethod & {
    type: typeof PaymentMethodTypes.OTHER
    data: PaymentMethodOtherData
  } {
    return this.type === PaymentMethodTypes.OTHER
  }

  isCheck(): this is PaymentMethod & {
    type: typeof PaymentMethodTypes.CHECK
    data: PaymentMethodCheckData
  } {
    return this.type === PaymentMethodTypes.CHECK
  }

  isACH(): this is PaymentMethod & {
    type: typeof PaymentMethodTypes.ACH
    data: PaymentMethodACHData
  } {
    return this.type === PaymentMethodTypes.ACH
  }

  isEFT(): this is PaymentMethod & {
    type: typeof PaymentMethodTypes.EFT
    data: PaymentMethodEFTData
  } {
    return this.type === PaymentMethodTypes.EFT
  }

  isProcurifyAch(): this is PaymentMethod & {
    type: typeof PaymentMethodTypes.PROCURIFY_ACH

    data: PaymentMethodProcurifyAchData
  } {
    return this.type === PaymentMethodTypes.PROCURIFY_ACH
  }

  isProcurifyWire(): this is PaymentMethod & {
    type: typeof PaymentMethodTypes.PROCURIFY_WIRE
    data: PaymentMethodProcurifyAchData
  } {
    return this.type === PaymentMethodTypes.PROCURIFY_WIRE
  }

  isProcurifyCheck(): this is PaymentMethod & {
    type: typeof PaymentMethodTypes.PROCURIFY_CHECK
    data: PaymentMethodProcurifyCheckData
  } {
    return this.type === PaymentMethodTypes.PROCURIFY_CHECK
  }

  isAirwallex(): this is PaymentMethod & {
    type: typeof PaymentMethodTypes.AIRWALLEX
    data: PaymentMethodAirwallexData
    paymentRail: string
    bankAccountCountryCode: CountryCode
  } {
    return this.type === PaymentMethodTypes.AIRWALLEX
  }

  isInternational(): boolean {
    if (!this.isAirwallex()) {
      return false
    }

    return !DOMESTIC_US_PAYMENT_RAILS.includes(this.paymentRail)
  }
}
